import React from "react";
import {
  FEATURED_ITEM_ENTITY_TYPE,
  FEATURED_ITEM_LINK_TYPE,
} from "common/constants";
import { buildLink } from "common/routing";
import { ButtonLink } from "../Common/ButtonLink";
import { Body } from "../Common/Typography";
import { ActionOptions } from "../Inputs/DropdownMenu";
import { Container, Image, TextContent } from "./__styles__/FeaturedItem";

type FeaturedItemImage = {
  url: string;
  accessibilityText: string;
};

type FeaturedItemLink = Omit<ActionOptions, "type"> & {
  text: string;
  type?: Maybe<FEATURED_ITEM_LINK_TYPE>;
};

const assertNever = (value: never): never => {
  throw new Error(
    `Unhandled entity type: ${value}. Add a case to handle this entity type.`,
  );
};

const getFeaturedItemLinkProps = (
  entityType: FEATURED_ITEM_ENTITY_TYPE,
  entityId: string,
  link: FeaturedItemLink,
): ActionOptions => {
  switch (entityType) {
    case FEATURED_ITEM_ENTITY_TYPE.WORKFLOW_DEFINITION:
      return {
        to: buildLink("workflowDefinition", {
          workflowDefinitionId: entityId,
        }),
        target: "_blank",
      };
    case FEATURED_ITEM_ENTITY_TYPE.SUBMISSION_TYPE:
      return {
        to: buildLink("createSubmission", {
          submissionTypeId: entityId,
        }),
        target: "_blank",
      };
    case FEATURED_ITEM_ENTITY_TYPE.SAVED_VIEW:
      if (link.type === FEATURED_ITEM_LINK_TYPE.MAP_LAYER) {
        return {
          to: buildLink("explore", {}, { savedViewId: entityId }),
          target: "_blank",
        };
      }
      return {
        to: buildLink("residentSavedView", {
          savedViewId: entityId,
        }),
        target: "_blank",
      };
    default:
      return assertNever(entityType);
  }
};

export type FeaturedItemProps = {
  image: FeaturedItemImage;
  title: string;
  description: string;
  link: FeaturedItemLink;
  entityType: FEATURED_ITEM_ENTITY_TYPE;
  entityId: string;
};

export const FeaturedItem = ({
  image,
  title,
  description,
  link,
  entityType,
  entityId,
}: FeaturedItemProps) => {
  const linkProps = getFeaturedItemLinkProps(entityType, entityId, link);

  return (
    <Container>
      <Image src={image.url} alt={image.accessibilityText} />
      <TextContent>
        <Body size="small" type="emphasis" style={{ marginBottom: 0 }}>
          {title}
        </Body>
        <Body
          size="small"
          type="regular"
          color="contentSecondary"
          style={{ marginBottom: 0 }}
        >
          {description}
        </Body>
      </TextContent>
      {link && (
        <ButtonLink
          styleVariant="primary"
          size="small"
          rightIconName="arrow-right"
          {...linkProps}
        >
          {link.text}
        </ButtonLink>
      )}
    </Container>
  );
};
